import { computed } from 'vue';
import { canReadOrganizationMembers, useOrganizationEntitlement } from '#layers/access/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { useOrgBranding } from './use-org-branding';

/**
 * Organization header summary shared by the settings-overlay Header and the workspace switcher:
 * the org settings singleton (logo/name) and display name from `useOrgBranding`, plus the
 * non-service member count.
 *
 * The count comes from the engine's `meta.totalCount` (which a paginated query requests): a plain
 * list read is capped at the backend listing limit and would undercount large orgs.
 */
export function useOrgSummary() {
	const store = useSystemEntityStore();
	const { orgSettings, orgName } = useOrgBranding();
	const { can: canOrganization } = useOrganizationEntitlement();
	const canReadMembers = computed(() => canOrganization(canReadOrganizationMembers));

	const membersQuery = store.MonospaceUser.query((q) => canReadMembers.value
		? q.many({
				pageIndex: 0,
				pageSize: 1,
				filter: { isService: false },
			})
		: q.many({ enabled: false }));
	// `undefined` until the count resolves, so callers can avoid rendering "No members" mid-load.
	const memberCount = computed(() => canReadMembers.value ? membersQuery.meta.value?.totalCount : undefined);

	return { orgSettings, orgName, memberCount };
}
